import { Fragment } from 'react';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { Eyebrow } from '../../components/Eyebrow';
import type { SkuBenchmark, SkuUsage } from '../../data/analytics';
import type { ApiDetail, CategoryId, Endpoint } from '../../features/catalog/model';
import { useCatalogLinks } from '../../components/CatalogLinks';
import { canChartUsage } from '../../lib/networkUsage';
import { hasBenchmarkPopulation } from '../../lib/benchmarks';
import { MeasuredCards } from '../../components/MeasuredCards';
import { PageHeader } from '../../components/editorial/PageHeader';
import { PlatformMark } from '../../components/PlatformMark';
import { m } from '../../paraglide/messages';

/** Renders a REST path, accenting `:param` and `?query` segments. */
const PathParts = ({ path }: { path: string }) => {
  const parts = path.split(/(:[^/?]+|\?[^/]*)/g).filter(Boolean);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith(':') || p.startsWith('?') ? (
          <span key={i} className="text-accent">
            {p}
          </span>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        ),
      )}
    </>
  );
};

export interface EndpointHeaderProps {
  api: ApiDetail;
  endpoint: Endpoint;
  /** the platform's fallback HugeIcons name from the catalog list */
  icon: string;
  /** the endpoint's real traffic, when it has any */
  usage?: SkuUsage;
  /** the endpoint's current-window measurement, when it has one */
  benchmark?: SkuBenchmark;
  /** the gateway's own label for the health window, e.g. `30d` */
  window?: string;
}

/** Endpoint detail header, as the page's opening `Band`: crumb, the platform's
 *  mark beside the h1, description, the method chip on the mono path, and the
 *  three measured cards.
 *
 *  The mark is the same object the platform page draws, so a reader landing on
 *  one endpoint recognises the platform without reading. It takes the slot the
 *  method chip used to hold, and the method moves onto the path it names.
 *
 *  Nothing sits inside the heading; nested, a chip prefixed the accessible name
 *  so every endpoint page announced itself as "POSTAds search" and named
 *  neither its platform nor an API. Price is not repeated here - it lives in
 *  the provider table below, which is also the only place the call order is
 *  stated. */
export const EndpointHeader = ({
  api,
  endpoint,
  icon,
  usage,
  benchmark,
  window,
}: EndpointHeaderProps) => {
  const links = useCatalogLinks();
  const showUptime = benchmark
    ? hasBenchmarkPopulation(benchmark.uptimeSample)
    : false;
  const showLatency = benchmark
    ? hasBenchmarkPopulation(benchmark.latencySample)
    : false;
  return (
    <PageHeader
      eyebrow={<Eyebrow to={links.api(api.id)}>{m.endpoint_header_eyebrow({ platform: api.name })}</Eyebrow>}
      title={
        <ParaglideMessage
          message={m.endpoint_header_title}
          inputs={{ platform: api.name, action: endpoint.action }}
          markup={{ muted: ({ children }) => <span className="muted-word">{children}</span> }}
        />
      }
      /* The platform's mark sits beside the h1, never inside it. */
      mark={
        <PlatformMark
          id={api.id}
          icon={icon}
          category={api.category as CategoryId}
          size={40}
          iconSize={22}
        />
      }
      /* Full text, not clamped: at this width a description this short is one
         line anyway, and nothing is hidden when it is not. */
      lede={endpoint.description}
    >
      <div className="mono flex items-center gap-2 overflow-x-auto whitespace-nowrap text-13 text-text-2">
        <span className="rounded-6 border border-accent-line bg-accent-soft px-2 py-0.5 text-12 font-medium tracking-[0.04em] text-accent">
          {endpoint.method}
        </span>
        <PathParts path={endpoint.path} />
      </div>

      <MeasuredCards
        className="mt-5"
        uptimePct={showUptime && benchmark ? benchmark.uptimePct : undefined}
        uptimeSample={benchmark?.uptimeSample}
        latencyP50Ms={showLatency && benchmark ? benchmark.latencyP50Ms : undefined}
        window={window}
        requests={usage?.requests ?? 0}
        weekly={canChartUsage(usage) ? usage.weekly : undefined}
      />
    </PageHeader>
  );
};
